import { textGray, textRed } from '@osmosis/tailwind-ui/styles/text';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { P } from '@osmosis/tailwind-ui/components/Text';
import React, { useEffect, useState } from 'react';
import { faFire } from '@fortawesome/pro-solid-svg-icons/faFire';
import { faPlay } from '@fortawesome/pro-solid-svg-icons/faPlay';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { PlaylistProps, PlaylistType } from '@osmosis/shared/types/playlist';

import { FormattedMessage } from 'react-intl';
import { usePagesContext } from '../../../context/PagesProvider';
import { getPlaylistDuration } from '../../../lib/playlist';
import EditablePlaylistTitle from './EditablePlaylistTitle';
import PlaylistSettingsButton from './PlaylistSettingsButton';
import PlaylistQuizAllButton from './PlaylistQuizAllButton';
import { PROD_MEDIA_SERVER } from '@osmosis/constants';
import Avatar from '../../../components/Avatar';
import { useUserContext } from '../../../context/UserContext';
import {
  FeatureFlagsSet,
  useFeatureFlagsContext,
} from '../../../context/FeatureFlagsContext';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import { useEmbedContext } from '@osmosis/nextjs-components/context/EmbedProvider';

type Props = {
  playlist: PlaylistProps;
  searchToggle: () => void;
  learnUrl?: string;
};

const Dot = (): JSX.Element => {
  const dot = '•';

  return <Box className={`mx-2 opacity-25`}>{dot}</Box>;
};

const TopicsCount = ({ count }: { count: number }) => (
  <FormattedMessage
    id="learn.playlist.topics_count"
    defaultMessage="{count, plural, =0 {No topics} one {1 topic} other {{count} topics}}"
    values={{
      count,
    }}
  />
);

const FollowerCount = ({ count }: { count: number }) => (
  <FormattedMessage
    id="learn.playlist.follower_count"
    defaultMessage="{count, plural, =0 {No followers} one {1 follower} other {{count} followers}}"
    values={{
      count,
    }}
  />
);

/**
 * PlaylistVideosHeader.
 */
const PlaylistVideosHeader = (props: Props): JSX.Element => {
  const { playlist, searchToggle, learnUrl } = props;

  const { push } = usePagesContext();
  const { user } = useUserContext();
  const { isInIframe } = useEmbedContext();

  const getPlaylistFollowers = () => playlist.followers.length;
  const getPlaylistFollowing = () =>
    !!(
      playlist.type === PlaylistType.FOLLOWING &&
      user &&
      playlist.followers.includes(user.userID)
    );

  const [followersCount, setFollowersCount] = useState(getPlaylistFollowers());
  const [isFollowed, setFollowed] = useState(getPlaylistFollowing());

  const handleFollowing = () => {
    setFollowed((prev) => !prev);
    setFollowersCount((prev) => (isFollowed ? prev - 1 : prev + 1));
  };

  const playlistCreator = playlist.user;
  const avatarProps = {
    preferredName: playlistCreator.preferredName,
    lastName: playlistCreator.lastName,
    avatar: playlistCreator.avatar
      ? playlistCreator.avatar.includes('http')
        ? playlistCreator.avatar
        : `${PROD_MEDIA_SERVER}${playlistCreator.avatar}`
      : '',
  };
  const isPlaylistMine = playlist.userID === user?.userID && playlist.userOwned;
  const playlistLength = (playlist?.list || []).length;
  const playlistDuration = getPlaylistDuration(
    (playlist?.list || []).reduce(
      (acc, el) => acc + +(el.data?.length || 0),
      0,
    ),
  );

  function dateToLocal(date: string) {
    return new Date(date).toLocaleDateString().replace(/\//g, '-');
  }

  const playlistCreationString = 'Created: ';
  const playlistModifiedString = 'Last edited: ';
  const playlistCreationDate = dateToLocal(playlist.created);
  const playlistModifiedDate = dateToLocal(playlist.modified);

  const hasVideo = playlist.list.some((item) => {
    const { data } = item;
    return typeof data?.subType === 'number';
  });

  const featureFlags = useFeatureFlagsContext();

  useEffect(() => {
    if (followersCount !== playlist.followers.length) {
      setFollowersCount(getPlaylistFollowers());
    }
    setFollowed(getPlaylistFollowing());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlist?.url]);

  const handlePlayAllClick = () => {
    const url = `${learnUrl}?from=/playlist/${playlist.url}`;
    if (isInIframe) {
      window.parent.location.href = url;
      return;
    }
    push(url);
  };

  return (
    <Box className={`p-4`}>
      <Flex className={`mb-2 justify-between`}>
        {/* Editable title */}
        <EditablePlaylistTitle
          playlist={playlist}
          isPlaylistMine={isPlaylistMine}
        />

        {/* Settings button */}
        <PlaylistSettingsButton
          playlist={playlist}
          isFollowed={isFollowed}
          handleFollowing={handleFollowing}
        />
      </Flex>
      <Flex className={`mb-4`}>
        <Box className={`mr-4`}>
          <Avatar {...avatarProps} />
        </Box>
        <Flex className={`items-center`}>
          {playlistCreator && (
            <>
              <P>{playlistCreator.preferredName}</P>
              <Dot />
            </>
          )}

          <P>
            <TopicsCount count={playlistLength} />
          </P>
          <Dot />
          <P>{playlistDuration}</P>
          <Dot />
          <P>
            <strong>{playlistCreationString}</strong>
            {playlistCreationDate}
          </P>
          <Dot />
          <P>
            <strong>{playlistModifiedString}</strong>
            {playlistModifiedDate}
          </P>
          {followersCount > 0 && (
            <>
              <Dot />
              <Flex className={`items-center`}>
                <Icon icon={faFire} className={`${textRed}`} />
                &nbsp;
                <P data-testid="followers-count">
                  <FollowerCount count={followersCount} />
                </P>
              </Flex>
            </>
          )}
        </Flex>
      </Flex>
      {user && learnUrl && (
        <Flex className="gap-2">
          {hasVideo && (
            <Button
              startIcon={<Icon icon={faPlay} />}
              data-testid="play-all-playlist-btn"
              onClick={handlePlayAllClick}
            >
              <FormattedMessage
                id="learn.playlist.Play-all"
                defaultMessage="Play all"
              />
            </Button>
          )}

          {/* Quiz button */}
          {<PlaylistQuizAllButton playlist={playlist} />}

          {/* Build Schedule button */}
          {featureFlags.isActive(FeatureFlagsSet.schedulesShare) && (
            <Button
              as="a"
              data-testid="playlist-build-schedule-button"
              variant="outline"
              href={`/schedule/new?playlistID=${playlist.playlistID}&name=${playlist.title}`}
            >
              <FormattedMessage
                id="playlist.build-schedule-button"
                defaultMessage="Build schedule"
              />
            </Button>
          )}

          {isPlaylistMine && (
            <>
              <Button
                data-testid="search-toggle-button"
                variant="outline"
                startIcon={<Icon icon={faPlus} className="-mt-1" />}
                onClick={searchToggle}
                // eslint-disable-next-line @nx/workspace/tailwind-style-enforcement
                className={cn(textGray)}
              >
                <FormattedMessage
                  id="learn.playlist.add"
                  defaultMessage="Add"
                />
              </Button>
            </>
          )}
        </Flex>
      )}
    </Box>
  );
};

export default PlaylistVideosHeader;
